import { Color, Spacing } from '@persona-id/design-tokens';
import { CaretDownIcon, CaretUpIcon } from '@persona-id/design-tokens/icons';
import {
  ChangeEvent,
  FocusEvent,
  FocusEventHandler,
  KeyboardEventHandler,
  MouseEvent,
  MutableRefObject,
  useState,
} from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled from 'styled-components';

import {
  ControlConfig,
  ControlConfigNumberPicker,
  processNumberValue,
} from 'dashboard/components/FormControlPilledInput/types';
import { NumberValue, ValueType } from 'dashboard/models/graph-query';
import { paragraphMixins } from 'design-system/mixins/text';

interface EditableItemNumberPickerProps {
  controlConfig: ControlConfig;
  deleteItem: () => void;
  deleteLastItem: () => void;
  inputRef: MutableRefObject<HTMLInputElement>;
  isLast: boolean;
  itemKey: string;
  value: NumberValue;
  onValueChange: (value: NumberValue) => void;
  onBlur?: FocusEventHandler<HTMLInputElement>;
}

const EditableNumberPicker = styled('input')`
  ${paragraphMixins.md};
  &::placeholder {
    font-weight: ${({ theme }) => theme.text.paragraph.md.fontWeight};
    color: ${({ theme }) => theme.color.text.tertiary};
  }

  background: none;

  display: inline;
  min-width: 40px;
  width: unset;
  padding: ${Spacing[1]} 0;
`;

const Container = styled('div')`
  display: inline-block;
  position: relative;
`;

const UpDownContainer = styled('div')`
  display: flex;
  flex-direction: column;
  position: absolute;
  top: 0;
  right: -12px;
  border: 1px solid ${Color.CoreSlate100};
  border-radius: ${BORDER_RADIUSES.rounded};
`;

const IncreaseIcon = styled(CaretUpIcon)`
  width: 12px;
  height: 12px;
  color: ${Color.CoreSlate200};

  &:hover {
    color: ${Color.CoreSlate1000};
  }

  &:active {
    background: ${Color.CoreSlate50};
  }
`;

const DecreaseIcon = styled(CaretDownIcon)`
  width: 12px;
  height: 12px;
  color: ${Color.CoreSlate200};

  &:hover {
    color: ${Color.CoreSlate1000};
  }

  &:active {
    background: ${Color.CoreSlate50};
  }
`;

const Spacer = styled('div')`
  height: 2px;
`;

const EditableItemNumberPicker = ({
  controlConfig,
  deleteItem,
  deleteLastItem,
  inputRef,
  isLast,
  itemKey,
  onValueChange,
  value: initialValue,
}: EditableItemNumberPickerProps) => {
  const [value, setValue] = useState<string>(initialValue['raw-value']);
  const [focused, setFocused] = useState<boolean>(false);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  };

  const commitValue = () => {
    const processedValue = processNumberValue(
      {
        ...initialValue,
        'raw-value': value,
        type: ValueType.Number,
      },
      controlConfig as ControlConfigNumberPicker,
    );

    onValueChange(processedValue);

    // TODO: See if we can remove this by changing 'defaultValue' and
    // making use of useEffect
    setValue('');
  };

  const increase = () => {
    const processedValue = processNumberValue(
      {
        ...initialValue,
        'raw-value': value,
        type: ValueType.Number,
      },
      controlConfig as ControlConfigNumberPicker,
    );

    if (processedValue.value != null) {
      const newValue = processedValue.value + 1;
      setValue(newValue.toString());
    } else {
      setValue('1');
    }
  };

  const decrease = () => {
    const processedValue = processNumberValue(
      {
        ...initialValue,
        'raw-value': value,
        type: ValueType.Number,
      },
      controlConfig as ControlConfigNumberPicker,
    );

    if (processedValue.value != null) {
      const newValue = processedValue.value - 1;
      setValue(newValue.toString());
    } else {
      setValue('0');
    }
  };

  const handleIncreaseClick = (e: MouseEvent<SVGSVGElement>) => {
    e.preventDefault();
    e.stopPropagation();
    increase();
  };

  const handleDecreaseClick = (e: MouseEvent<SVGSVGElement>) => {
    e.preventDefault();
    e.stopPropagation();
    decrease();
  };

  const handleKeyPress: KeyboardEventHandler = (e) => {
    if (e.key === 'Enter' && value.trim() !== '') {
      e.preventDefault();
      commitValue();
    }
  };

  const handleKeyDown: KeyboardEventHandler = (e) => {
    if (e.key === 'Tab' && value.trim() !== '') {
      e.preventDefault();

      commitValue();
    }

    if (e.key === 'Backspace' && value.trim() === '') {
      if (isLast) {
        deleteLastItem();
      } else {
        deleteItem();
      }
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      increase();
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      decrease();
    }
  };

  const handleFocus = (_e: FocusEvent<HTMLInputElement>) => {
    setFocused(true);
  };

  const handleBlur = () => {
    setFocused(false);
  };

  return (
    <Container>
      <OutsideClickHandler onOutsideClick={handleBlur}>
        <EditableNumberPicker
          key={itemKey}
          size={value?.length || 3}
          ref={(input) => {
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            inputRef.current = input;
          }}
          value={value}
          onChange={handleChange}
          onKeyPress={handleKeyPress}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
        />
        {focused && (
          <UpDownContainer>
            <IncreaseIcon
              id='form-control-pilled-input__editable_item_number_picker-up_arrow'
              onClick={handleIncreaseClick}
            />
            <Spacer></Spacer>
            <DecreaseIcon
              id='form-control-pilled-input__editable_item_number_picker-down-arrow'
              onClick={handleDecreaseClick}
            />
          </UpDownContainer>
        )}
      </OutsideClickHandler>
    </Container>
  );
};

export default EditableItemNumberPicker;
